import { createAsyncThunk, createEntityAdapter, createSlice } from "@reduxjs/toolkit";
import { restClient } from "@/src/services/restClient";
const regulationsAdapter = createEntityAdapter({
  sortComparer: (a, b) => a.title.localeCompare(b.title)
});
const initialState = regulationsAdapter.getInitialState({
  status: "idle",
  error: null
});
const toMessage = (error, fallback) => {
  if (typeof error === "string" && error.trim()) return error;
  if (error instanceof Error) return error.message;
  return fallback;
};
export const fetchRegulations = createAsyncThunk("regulations/fetchAll", async (_, {
  rejectWithValue
}) => {
  try {
    return await restClient.get("/regulations");
  } catch (error) {
    return rejectWithValue(toMessage(error, "Failed to load regulations"));
  }
});
export const createRegulationThunk = createAsyncThunk("regulations/create", async (payload, {
  rejectWithValue
}) => {
  try {
    return await restClient.post("/regulations", payload);
  } catch (error) {
    return rejectWithValue(toMessage(error, "Failed to create regulation"));
  }
});
export const updateRegulationThunk = createAsyncThunk("regulations/update", async ({
  id,
  patch
}, {
  rejectWithValue
}) => {
  try {
    return await restClient.patch(`/regulations/${id}`, patch);
  } catch (error) {
    return rejectWithValue(toMessage(error, "Failed to update regulation"));
  }
});
export const deleteRegulationThunk = createAsyncThunk("regulations/delete", async (id, {
  rejectWithValue
}) => {
  try {
    await restClient.delete(`/regulations/${id}`);
    return id;
  } catch (error) {
    return rejectWithValue(toMessage(error, "Failed to delete regulation"));
  }
});
const regulationsSlice = createSlice({
  name: "regulations",
  initialState,
  reducers: {
    clearRegulationsError(state) {
      state.error = null;
    }
  },
  extraReducers: builder => {
    builder.addCase(fetchRegulations.pending, state => {
      state.status = "loading";
      state.error = null;
    }).addCase(fetchRegulations.fulfilled, (state, action) => {
      state.status = "succeeded";
      regulationsAdapter.setAll(state, action.payload);
    }).addCase(fetchRegulations.rejected, (state, action) => {
      state.status = "failed";
      state.error = action.payload ?? action.error.message ?? null;
    }).addCase(createRegulationThunk.fulfilled, (state, action) => {
      regulationsAdapter.upsertOne(state, action.payload);
    }).addCase(updateRegulationThunk.fulfilled, (state, action) => {
      regulationsAdapter.upsertOne(state, action.payload);
    }).addCase(deleteRegulationThunk.fulfilled, (state, action) => {
      regulationsAdapter.removeOne(state, action.payload);
    });
  }
});
export const {
  clearRegulationsError
} = regulationsSlice.actions;
export const regulationsSelectors = regulationsAdapter.getSelectors(state => state.regulations);
export const selectRegulationsList = regulationsSelectors.selectAll;
export const selectRegulationsStatus = state => state.regulations.status;
export const selectRegulationsError = state => state.regulations.error;
export default regulationsSlice.reducer;